// ═══════════════════════════════════════════════════════════
// RollCall — Check-in History
// All check-ins across events with search and filters
// ═══════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useRollCallStore } from '../../store/rollcall-store';
import { IconDownload } from '@tabler/icons-react';
import { useAuthStore } from '../../auth/auth-store';
import { GabBadge, GabButton, GabDataTable, GabSelect } from '../../components/gab';
import { GabInput } from '../../components/gab/GabInput';
import { notify } from '../../components/gab/GabNotifications';
import { fmtDateTime } from '../../lib/date-format';

const METHOD_COLORS: Record<string, 'primary' | 'success' | 'warning' | 'neutral'> = {
  pin: 'primary', badge: 'success', mobile: 'warning', manual: 'neutral', admin: 'primary',
};

export default function CheckinHistory() {
  const { events, checkins, fetchEvents, fetchCheckins } = useRollCallStore();
  const activeIncident = useAuthStore(s => s.activeIncident);
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState<string | null>(null);

  useEffect(() => { fetchEvents(); fetchCheckins(); }, []);

  const incidentEvents = useMemo(() =>
    activeIncident ? events.filter(e => String(e.incidentId) === String(activeIncident.id)) : events,
    [events, activeIncident]
  );

  const eventOptions = useMemo(() => incidentEvents.map(e => ({ value: String(e.id), label: e.eventName })), [incidentEvents]);

  const enriched = useMemo(() => {
    const eventMap = new Map(incidentEvents.map(e => [String(e.id), e]));
    return checkins
      .filter(c => {
        const evt = eventMap.get(String(c.eventId));
        if (!evt) return false;
        if (eventFilter && String(c.eventId) !== eventFilter) return false;
        if (search) {
          const q = search.toLowerCase();
          return (c.personName || '').toLowerCase().includes(q) || (c.agency || '').toLowerCase().includes(q) || (evt?.eventName || '').toLowerCase().includes(q);
        }
        return true;
      })
      .map(c => ({ ...c, eventName: eventMap.get(String(c.eventId))?.eventName || '' }))
      .sort((a, b) => (b.checkInTime || '').localeCompare(a.checkInTime || ''));
  }, [checkins, incidentEvents, eventFilter, search]);

  function exportCSV() {
    if (!enriched.length) return;
    const headers = ['Name', 'Agency', 'Event', 'Check-In', 'Check-Out', 'Method', 'Status'];
    const rows = enriched.map(c => [
      c.personName, c.agency, c.eventName,
      c.checkInTime || '', c.checkOutTime || '', c.method,
      c.checkOutTime ? 'Out' : 'In',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `checkin-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    notify({ title: 'Exported', variant: 'success' });
  }

  type Row = typeof enriched[0] & Record<string, unknown>;
  const columns = [
    { key: 'personName', label: 'Name', sortable: true, render: (r: Row) => <span className="fw-semibold">{r.personName}</span> },
    { key: 'agency', label: 'Agency' },
    { key: 'eventName', label: 'Event' },
    { key: 'checkInTime', label: 'Check-In', width: 160, render: (r: Row) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{fmtDateTime(r.checkInTime)}</span> },
    { key: 'checkOutTime', label: 'Check-Out', width: 160, render: (r: Row) => r.checkOutTime ? <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{fmtDateTime(r.checkOutTime)}</span> : <GabBadge variant="success">IN</GabBadge> },
    { key: 'method', label: 'Method', width: 80, render: (r: Row) => <GabBadge variant={METHOD_COLORS[r.method] || 'neutral'}>{r.method}</GabBadge> },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted">{enriched.length} check-ins</p>
        <GabButton variant="ghost" leftIcon={<IconDownload size={14} />} onClick={exportCSV} disabled={!enriched.length}>Export CSV</GabButton>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap items-end">
        <div style={{ flex: 1, minWidth: 200 }}>
          <GabInput placeholder="Search name, agency, event..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ width: 200 }}>
          <GabSelect placeholder="All Events" options={eventOptions} value={eventFilter} onChange={setEventFilter} clearable />
        </div>
      </div>

      <GabDataTable<Row> columns={columns} data={enriched as Row[]} rowKey={r => r.id} striped emptyMessage="No check-ins recorded." />
    </div>
  );
}
